import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Grid,
  Typography, Switch, FormControlLabel, CircularProgress, Alert, Tabs, Tab, IconButton, Chip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { uploadImage } from '../utils/imageUpload';

const LANGS = [
  { key: 'desc_en', label: 'English' },
  { key: 'desc_fi', label: 'Finnish' },
  { key: 'desc_de', label: 'German' },
  { key: 'desc_fr', label: 'French' },
];

const empty = {
  logo_url: '', hero_image: '', website: '', founded: '', slug: '',
  desc_en: '', desc_fi: '', desc_de: '', desc_fr: '',
  featured: 0, active: 1, sort_order: 0,
};

const BrandMetaDialog = ({ open, onClose, brand, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (brand) {
      setForm({
        logo_url: brand.logo_url || '', hero_image: brand.hero_image || '',
        website: brand.website || '', founded: brand.founded || '', slug: brand.slug || '',
        desc_en: brand.desc_en || '', desc_fi: brand.desc_fi || '',
        desc_de: brand.desc_de || '', desc_fr: brand.desc_fr || '',
        featured: brand.featured ? 1 : 0, active: brand.active ? 1 : 0,
        sort_order: brand.sort_order || 0,
      });
      setTab(0); setError('');
    }
  }, [brand]);

  if (!brand) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const url = await uploadImage(file);
      set('logo_url', url);
    } catch (err) {
      setError('Logo upload failed: ' + err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`https://api.elnaf.fi/api/brands_admin.php?marka_id=${brand.marka_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.result === 1) {
        onSaved();
      } else {
        setError(data.error || 'Save failed');
      }
    } catch (err) {
      setError('Network error while saving.');
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = `https://elnaf.fi/en/brands/${form.slug || brand.slug}`;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{brand.name}</Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            {brand.count} products · marka_id {brand.marka_id}
          </Typography>
        </Box>
        <Chip label={form.active ? 'Visible' : 'Hidden'} size="small"
              sx={{ ml: 'auto', backgroundColor: form.active ? '#dcfce7' : '#fee2e2', color: form.active ? '#166534' : '#991b1b', fontWeight: 600 }} />
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* Logo + flags */}
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b7280', display: 'block', mb: 1 }}>LOGO</Typography>
            <Box sx={{
              width: '100%', aspectRatio: '4/3', border: '1px dashed #d1d5db', borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fafafa',
              overflow: 'hidden', mb: 1.5, position: 'relative',
            }}>
              {form.logo_url
                ? <img src={form.logo_url} alt="logo" style={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain' }} />
                : <Typography variant="caption" sx={{ color: '#9ca3af' }}>No logo</Typography>}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button component="label" size="small" variant="outlined" fullWidth
                      startIcon={uploading ? <CircularProgress size={15} /> : <CloudUploadIcon />} disabled={uploading}
                      sx={{ textTransform: 'none', borderColor: '#845adf', color: '#845adf' }}>
                {uploading ? 'Uploading…' : 'Upload'}
                <input ref={fileRef} type="file" hidden accept="image/*" onChange={handleUpload} />
              </Button>
              {form.logo_url && (
                <IconButton size="small" color="error" onClick={() => set('logo_url', '')}><DeleteIcon fontSize="small" /></IconButton>
              )}
            </Box>

            <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FormControlLabel
                control={<Switch checked={!!form.featured} onChange={(e) => set('featured', e.target.checked ? 1 : 0)} />}
                label={<Typography variant="body2">Featured on /brands</Typography>} />
              <FormControlLabel
                control={<Switch checked={!!form.active} onChange={(e) => set('active', e.target.checked ? 1 : 0)} />}
                label={<Typography variant="body2">Show brand page</Typography>} />
            </Box>
          </Grid>

          {/* Fields */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth size="small" label="URL slug"
                           value={form.slug} onChange={(e) => set('slug', e.target.value)}
                           helperText="Leave blank to auto-generate from the name" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Founded" placeholder="1911, USA"
                           value={form.founded} onChange={(e) => set('founded', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Manufacturer website" placeholder="https://www.eaton.com"
                           value={form.website} onChange={(e) => set('website', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b7280' }}>
                    DESCRIPTION (200–300 words is ideal for SEO)
                  </Typography>
                </Box>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 34, mb: 1, '& .MuiTab-root': { minHeight: 34, textTransform: 'none' } }}>
                  {LANGS.map((l) => <Tab key={l.key} label={l.label} />)}
                </Tabs>
                <TextField fullWidth multiline rows={7}
                           value={form[LANGS[tab].key]} onChange={(e) => set(LANGS[tab].key, e.target.value)}
                           placeholder={`${brand.name} — brand story, product range, certifications… (${LANGS[tab].label})`} />
                <Typography variant="caption" sx={{ color: '#9ca3af', mt: 0.5, display: 'block' }}>
                  {(form[LANGS[tab].key] || '').trim().split(/\s+/).filter(Boolean).length} words
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e5e7eb', px: 3, py: 2 }}>
        <Button href={previewUrl} target="_blank" startIcon={<OpenInNewIcon />} sx={{ mr: 'auto', textTransform: 'none', color: '#6b7280' }}>
          Preview page
        </Button>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ backgroundColor: '#845adf', '&:hover': { backgroundColor: '#6c48c5' } }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrandMetaDialog;
